import React, { useEffect, useRef } from 'react';

const GoldenParticles = () => {
  const canvasRef = useRef(null);
  const animationFrameId = useRef(null);
  const mousePosition = useRef({ x: null, y: null });

  // HSLカラーを計算する関数
  const getHSLColor = (hue, saturation, lightness, alpha) => {
    return `hsla(${hue}, ${saturation}%, ${lightness}%, ${alpha})`;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // ウィンドウサイズに合わせてキャンバスサイズを設定
    const updateCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);

    // マウス位置の追跡
    const handleMouseMove = (e) => {
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      mousePosition.current = {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY
      };
    };

    const handleMouseLeave = () => {
      mousePosition.current = { x: null, y: null };
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);

    const ctx = canvas.getContext('2d');
    const particles = [];
    const particleCount = 100;

    // パーティクルの初期化
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 3 + 1,
        baseSize: Math.random() * 3 + 1,
        speedX: Math.random() * 1 - 0.5,
        speedY: -Math.random() * 1 - 0.5,
        alpha: Math.random() * 0.5 + 0.3,
        baseAlpha: Math.random() * 0.5 + 0.3,
        hue: 51,
        targetHue: 51,
        saturation: 100,
        lightness: 50,
        angle: Math.random() * Math.PI * 2,
        angleSpeed: (Math.random() * 0.02 - 0.01)
      });
    }

    // 2点間の距離を計算
    const getDistance = (x1, y1, x2, y2) => {
      return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    };

    // アニメーションループ
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach(particle => {
        // マウスとの相互作用
        if (mousePosition.current.x !== null) {
          const distance = getDistance(
            particle.x,
            particle.y,
            mousePosition.current.x,
            mousePosition.current.y
          );

          const interactionRadius = 150; // マウスの影響範囲を広げる
          if (distance < interactionRadius) {
            const angle = Math.atan2(
              particle.y - mousePosition.current.y,
              particle.x - mousePosition.current.x
            );
            
            const force = (1 - distance / interactionRadius) * 3; // 力を強める
            particle.speedX += Math.cos(angle) * force;
            particle.speedY += Math.sin(angle) * force;

            // 色の変化
            const targetHue = (mousePosition.current.x / canvas.width * 360) % 360;
            particle.targetHue = targetHue;
            particle.saturation = 100;
            particle.lightness = 60;
            particle.size = particle.baseSize * (1 + force * 0.5);
            particle.alpha = Math.min(0.8, particle.baseAlpha + force * 0.3);
          } else {
            // 通常の状態に徐々に戻す
            particle.targetHue = 51;
            particle.saturation = 100;
            particle.lightness = 50;
            particle.size = particle.baseSize;
            particle.alpha = particle.baseAlpha;
          }

          // 色を徐々に変化させる
          const hueDiff = particle.targetHue - particle.hue;
          particle.hue += hueDiff * 0.1;

          // 速度の減衰
          particle.speedX *= 0.95;
          particle.speedY *= 0.95;
        }

        // 基本の動き（マウスがない場合も動く）
        particle.x += particle.speedX;
        particle.y += particle.speedY;

        // 角度を更新して円を描くような動きを追加
        particle.angle += particle.angleSpeed;
        
        // より複雑な揺らぎパターン
        const time = Date.now() * 0.001;
        const swayX = Math.sin(particle.angle + time) * 0.5;
        const swayY = Math.cos(particle.angle + time * 0.7) * 0.5;
        
        particle.x += swayX;
        particle.y += swayY;

        // 通常状態での速度の減衰を緩やかに
        if (!mousePosition.current.x) {
          particle.speedX *= 0.99;
          particle.speedY *= 0.99;
          
          // 速度が小さくなりすぎたら新しい速度を設定
          if (Math.abs(particle.speedX) < 0.1 && Math.abs(particle.speedY) < 0.1) {
            particle.speedX = Math.random() * 1 - 0.5;
            particle.speedY = -Math.random() * 1 - 0.5;
          }
        } else {
          // マウス相互作用時は通常の減衰
          particle.speedX *= 0.95;
          particle.speedY *= 0.95;
        }

        // パーティクルの描画
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = getHSLColor(particle.hue, particle.saturation, particle.lightness, particle.alpha);
        ctx.fill();

        // 画面外に出たパーティクルを再配置
        if (particle.y < -10) {
          particle.y = canvas.height + 10;
          particle.x = Math.random() * canvas.width;
          particle.speedX = Math.random() * 1 - 0.5;
          particle.speedY = -Math.random() * 1 - 0.5;
          particle.angle = Math.random() * Math.PI * 2;
        }
        if (particle.x < -10) {
          particle.x = canvas.width + 10;
          particle.speedX = Math.abs(particle.speedX); // 右向きの速度を設定
        }
        if (particle.x > canvas.width + 10) {
          particle.x = -10;
          particle.speedX = -Math.abs(particle.speedX); // 左向きの速度を設定
        }
      });

      animationFrameId.current = requestAnimationFrame(animate);
    };

    // アニメーション開始
    animate();

    // クリーンアップ
    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      window.removeEventListener('resize', updateCanvasSize);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1
      }}
    />
  );
};

export default GoldenParticles; 